import {
    RadioGroup,
    type RadioGroupItem,
    type RadioGroupSize,
    type RadioGroupType,
} from '@trustoo/components/RadioGroup/v2'
import React, { ReactNode, useState } from 'react'
import { CodeSnippet, DemoAvatar, InlineCode, MIcon, Section } from './shared'

const RADIO_GROUP_USAGE = `import { RadioGroup } from '@trustoo/components/RadioGroup/v2'

const plans = [
  { value: 'basic', title: 'Basic plan', subtext: '$10/month',
    supportingText: 'Includes up to 10 users and 20 GB of data.' },
  { value: 'business', title: 'Business plan', subtext: '$20/month',
    supportingText: 'Includes up to 20 users and 40 GB of data.' },
]

// Single-select with a leading radio (the default type)
<RadioGroup items={plans} value={plan} onChange={setPlan} />

// Multi-select with checkbox controls (reuses Checkbox/v2)
<RadioGroup items={plans} type="checkbox" multiple value={picked} onChange={setPicked} />

// Icon, avatar and payment types render each item's \`leading\` slot
<RadioGroup items={plansWithIcons} type="icon-card" value={plan} onChange={setPlan} />`

const TYPES: { type: RadioGroupType; label: string }[] = [
    { type: 'radio-button', label: 'Radio button' },
    { type: 'checkbox', label: 'Checkbox' },
    { type: 'icon-simple', label: 'Icon simple' },
    { type: 'icon-card', label: 'Icon card' },
    { type: 'avatar', label: 'Avatar' },
    { type: 'payment-icon', label: 'Payment icon' },
]

const PaymentMark: React.FC<{ label: string }> = ({ label }) => (
    <span className="flex h-full w-full items-center justify-center text-xs font-bold">
        {label}
    </span>
)

const leadingFor = (type: RadioGroupType, index: number): ReactNode => {
    if (type === 'avatar') {
        return <DemoAvatar />
    }
    if (type === 'payment-icon') {
        return <PaymentMark label={['VISA', 'MC', 'AMEX'][index]} />
    }
    if (type === 'icon-simple' || type === 'icon-card') {
        return <MIcon name={['bolt', 'layers', 'workspace_premium'][index]} />
    }
    return undefined
}

const planItems = (type: RadioGroupType): RadioGroupItem[] =>
    [
        {
            value: 'basic',
            title: 'Basic plan',
            subtext: '$10/month',
            supportingText:
                'Includes up to 10 users, 20 GB individual data and access to all features.',
        },
        {
            value: 'business',
            title: 'Business plan',
            subtext: '$20/month',
            supportingText:
                'Includes up to 20 users, 40 GB individual data and access to all features.',
        },
        {
            value: 'enterprise',
            title: 'Enterprise plan',
            subtext: '$40/month',
            supportingText: 'Currently unavailable on your account — contact sales to upgrade.',
            disabled: true,
        },
    ].map((item, index) => ({ ...item, leading: leadingFor(type, index) }))

const StatefulRadioGroup: React.FC<{
    size?: RadioGroupSize
    type?: RadioGroupType
    multiple?: boolean
}> = ({ size = 'md', type = 'radio-button', multiple = false }) => {
    const [value, setValue] = useState<string | string[]>(multiple ? ['basic'] : 'basic')
    return (
        <RadioGroup
            items={planItems(type)}
            size={size}
            type={type}
            multiple={multiple}
            value={value}
            onChange={setValue}
        />
    )
}

export const RadioGroupShowcase: React.FC = () => (
    <Section
        title="RadioGroup (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Radio group</span> set
                (ComponentSet <InlineCode>5:20058</InlineCode>). A stack of selectable option cards
                driven by an <InlineCode>items[]</InlineCode> API, with one layout per Figma{' '}
                <InlineCode>Type</InlineCode>. It <span className="font-semibold">composes</span>{' '}
                the design-system controls — <InlineCode>Radio/v2</InlineCode>, or{' '}
                <InlineCode>Checkbox/v2</InlineCode> for{' '}
                <InlineCode>type=&quot;checkbox&quot;</InlineCode>. Import via{' '}
                <InlineCode>@trustoo/components/RadioGroup/v2</InlineCode>.
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Types</h3>
                <p className="text-xs text-text-tertiary">
                    Each <InlineCode>type</InlineCode> changes where the control sits and how the
                    item&apos;s <InlineCode>leading</InlineCode> slot is framed. The third card is{' '}
                    <InlineCode>disabled</InlineCode>.
                </p>
                <div className="grid grid-cols-1 gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-background-bg-secondary p-spacing-xl lg:grid-cols-2">
                    {TYPES.map(({ type, label }) => (
                        <div key={type} className="space-y-spacing-md">
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {label}
                            </span>
                            <StatefulRadioGroup type={type} />
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">
                    Multi-select (<InlineCode>type=&quot;checkbox&quot;</InlineCode> +{' '}
                    <InlineCode>multiple</InlineCode>)
                </h3>
                <p className="text-xs text-text-tertiary">
                    Each leading control is a reused <InlineCode>Checkbox/v2</InlineCode> and{' '}
                    <InlineCode>value</InlineCode> is a <InlineCode>string[]</InlineCode>.
                </p>
                <div className="rounded-radius-xs border border-border-secondary bg-background-bg-secondary p-spacing-xl">
                    <StatefulRadioGroup type="checkbox" multiple />
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="grid grid-cols-1 gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-background-bg-secondary p-spacing-xl lg:grid-cols-2">
                    <div className="space-y-spacing-md">
                        <span className="text-xs font-medium uppercase text-text-tertiary">sm</span>
                        <StatefulRadioGroup size="sm" />
                    </div>
                    <div className="space-y-spacing-md">
                        <span className="text-xs font-medium uppercase text-text-tertiary">md</span>
                        <StatefulRadioGroup size="md" />
                    </div>
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={RADIO_GROUP_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>items[]</InlineCode> ({'{'} value, title, subtext?,
                    supportingText?, leading?, badge?, actions?, disabled? {'}'}),{' '}
                    <InlineCode>size</InlineCode> (sm · md), <InlineCode>type</InlineCode>{' '}
                    (radio-button · checkbox · icon-simple · icon-card · avatar · payment-icon),{' '}
                    <InlineCode>multiple</InlineCode> (<InlineCode>value: string</InlineCode> →{' '}
                    <InlineCode>string[]</InlineCode>), <InlineCode>value</InlineCode> /{' '}
                    <InlineCode>defaultValue</InlineCode> + <InlineCode>onChange</InlineCode>,{' '}
                    <InlineCode>name</InlineCode>, and <InlineCode>disabled</InlineCode>.
                </p>
            </div>
        </div>
    </Section>
)
